import React, { useState, useRef, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Typography } from '@material-ui/core';
import { Box, Grid } from '@mui/material';
import {
	STORE_DRILLDOWN_HEADER_TEXT,
	GUIDED_INSIGHTS_RCA,
	AG_GRID_AUTOCOL,
	DOWNLOAD_REQUEST_EVENT,
	DOWNLOAD_REQUEST_EVENT_DESC_ROOTCAUSE,
	SUB_CATEGORY_TAB,
} from '../../constants/constant';
import { Breadcrumb } from '../Breadcrumb';
import {
	useParams 
} from 'react-router-dom';
import STORESVG from '../../assets/SVGs/store.svg';
import ItemFilter from './ItemFilter';
import DVTable from '../DVTable';
import { useStyles } from './style';
import { storeDrilldownColumns } from './storeDrilldownColumnsDefinitions';
import { getValueForExcel } from '../Table/utils';
import { saveEventTrack } from '../../redux/actions/eventTrackAction';
import { UserPortalContext } from '../../context/appcontext';
import { geStoreDrillDownData } from '../../redux/actions/storeDrillDownActions';
import { getStoreDrillDownPayload } from '../../helper/helper';
import downloadIcon from '../../assets/SVGs/download-icon.svg';
import { IconButton } from '@material-ui/core';
import { ShimmerStoreDrilldown } from './ShimmerStoreDrilldown';
import Filter from '../Filter/Filter';
import { updateInventoryRootCause } from '../../redux/actions/rcaActions';
import SomethingWentWrong from '../../common/components/SomethingWentWrong';

export const StoreDrilldownComponent = () => {
	// Route params
	let {
		itemId, // Item Number
		threshold, // selected threshold
		type,
		id,
		name,
		anomalyChange,
	} = useParams();

	const classes = useStyles();
	const [columnDef, setColumnDef] = useState([]);
	const [selectedWmItemNbr, setSelectedWmItemNbr] = useState(itemId);
	const [currentFilters, setCurrentFilters] = useState({});
	const [weekSelectedFilter, setWeekSelectedFilter] = useState();
	const [
		externalFilters,
		//setExternalFilters
	] = useState({});

	const gridRef = useRef();
	const portalUserContext = useContext(UserPortalContext);
	const capitalizeTypeText =
		type === 'subcategory'
			? SUB_CATEGORY_TAB
			: type.charAt(0).toUpperCase() + type.slice(1);

	const dispatch = useDispatch();

	// update reducer information to get column names
	const { tableRows, loading, error } = useSelector(
		(state) => state.storeDrillDownReducer
	);

	// update reducer information to get column names
	const { tableRows: itemDropDownValues } = useSelector(
		(state) => state.rcaReducer
	);

	const sessionWeekFilters = JSON.parse(sessionStorage.getItem('weekfilters'))

	useEffect(() => {
		setCurrentFilters(sessionWeekFilters);
		setWeekSelectedFilter(sessionWeekFilters?.weekFilter);

		if (!itemDropDownValues?.length > 0) {
			itemInformation();
		}

		if (sessionWeekFilters?.startDate && sessionWeekFilters?.endDate) {
			dispatch(
				geStoreDrillDownData({
					...getStoreDrillDownPayload({
						selectedFilters: sessionWeekFilters,
						id,
						name,
						threshold,
						itemId: selectedWmItemNbr,
						weekSelectedFilter: sessionWeekFilters?.weekFilter,
					}),
				})
			);
		}

		return () => {
			sessionStorage.removeItem('storeDrilldownWeekfilters');
		};
	}, []);

	const { weekFilter, endWeek, startDate, endDate } = currentFilters;

	// get Item information
	const itemInformation = () => {
		const payload = {
			anomalyChange: anomalyChange,
			endDate: sessionWeekFilters?.endDate,
			id: [id],
			startDate: sessionWeekFilters?.startDate,
			type: type?.toUpperCase().replace(/ +/g, ''),
			threshold: threshold,
			name: name,
		};

		dispatch(
			updateInventoryRootCause({
				request: payload,
			})
		);
	};

	useEffect(() => {
		setWeekSelectedFilter(weekFilter);
		if (currentFilters?.startDate && currentFilters?.endDate) {
			dispatch(
				geStoreDrillDownData({
					...getStoreDrillDownPayload({
						selectedFilters: currentFilters,
						id,
						name,
						threshold,
						itemId: selectedWmItemNbr,
						weekSelectedFilter: weekFilter,
					}),
				})
			);
		}
	}, [ startDate, endDate, selectedWmItemNbr]);

	useEffect(() => {
		setColumnDef(
			storeDrilldownColumns(currentFilters.weekFilter, tableRows?.length || 0)
		);
	}, [tableRows, weekFilter, selectedWmItemNbr]);

	const handleDownload = () => {
		const downloadTime = new Date().toLocaleString()?.replace(',', ' ');
		gridRef.current?.api?.exportDataAsExcel({
			fileName: `Scintilla-Channel Performance-${downloadTime}`,
			sheetName: 'Data',
			shouldRowBeSkipped(params) {
				return params.node.data?.fullWidth;
			},
			processCellCallback(params) {
				return params.column.colDef.colId === AG_GRID_AUTOCOL
					? params?.node?.data?.description
					: getValueForExcel(params);
			},
		});

		const request = {
			event: DOWNLOAD_REQUEST_EVENT,
			event_description: DOWNLOAD_REQUEST_EVENT_DESC_ROOTCAUSE,
			attributes: GUIDED_INSIGHTS_RCA,
		};
		dispatch(saveEventTrack({ request, portalUserContext }));
	};

	const components = () => {
		if (loading) {
			return <ShimmerStoreDrilldown />;
		} else if (error) {
			return <SomethingWentWrong />;
		} else
			return (
				<Box className={classes.flexibleContainer}>
					<Box className={classes.container}>
						<Box className={classes.menuRow}>
							<Box className={classes.menuFilters}>
								<ItemFilter
									values={itemDropDownValues?.edges}
									handleChange={(value) => {
										setSelectedWmItemNbr(value);
									}}
									selectedValue={selectedWmItemNbr}
								/>
							</Box>
							<Box className={classes.menuFilters}>
								<Filter
									weekFilter={weekSelectedFilter}
									setWeekFilter={setWeekSelectedFilter}
									selectedFilter={currentFilters.weekFilter}
									startWeek={weekFilter}
									endWeek={endWeek}
									startDate={startDate}
									endDate={endDate}
									isStoreDrilldown={true}
									setCurrentFilters={setCurrentFilters}
								/>
							</Box>
						</Box>
						<Box className={classes.icon}>
							<IconButton
								size="medium"
								className={classes.button_ellipsis}
								onClick={handleDownload}>
								<img alt="Download" src={downloadIcon} />
							</IconButton>
						</Box>
					</Box>
					<DVTable
						columns={columnDef}
						data={tableRows}
						filters={externalFilters}
						isError={error}
						isStoreDrilldown={true}
						tableRef={gridRef}
						initialItemsPerPage={10}
					/>
				</Box>
			);
	};

	return (
		<Box
			className="ag-theme-scintilla"
			sx={{
				'.ag-cell': {
					fontFamily: 'Bogle-Regular !important',
				},
				'--ag-cell-horizontal-padding': '0px',
				'.ag-pinned-left-header, .ag-pinned-left-cols-container': {
					width: '150px !important',
					minWidth: '150px !important',
					maxWidth: '150px !important',
				},
				'.ag-column-first': {
					width: '150px !important',
				},
				'.ag-header-cell-comp-wrapper': {
					borderRight:
						'var(--ag-borders-critical) var(--ag-border-color) !important',
				},
				'.ag-header-group-cell': {
					borderRight:
						'var(--ag-borders-critical) var(--ag-border-color) !important',
				},
				'.ag-ltr .ag-cell': {
					borderRight:
						'var(--ag-borders-critical) var(--ag-border-color) !important',
				},
				'.ag-theme-alpine .ag-layout-auto-height .ag-center-cols-viewport': {
					minHeight: '500px',
				},
			}}>
			<Grid container>
				<Box className={classes.headerInsights}>
					<Box className={classes.pageHeader}>
						<Breadcrumb />
					</Box>
					<Box>
						<Typography className={classes.itemNameHeading}>
							{STORE_DRILLDOWN_HEADER_TEXT + threshold}
						</Typography>
					</Box>
					<Box>
						<Typography className={classes.channelFilter}>
							<img src={STORESVG} alt="STORE logo" />
							<Typography className={classes.subTextStyles}>
								{capitalizeTypeText + ': ' + id + ' ' + name}
							</Typography>
						</Typography>
					</Box>
				</Box>
				<Grid item xs={12} sm={12} md={12} lg={12}>
					{components()}
				</Grid>
			</Grid>
		</Box>
	);
};